import React, { useState, useMemo } from "react";
import { Compass, Filter } from "lucide-react";
import FlightCard from "../components/FlightCard";
import Seo from "../components/Seo";
import { exploreDeals, REGIONS, CABINS, isDateLocked, FREE_WINDOW_DAYS } from "../data/mock";
import ProGate, { useIsPro } from "../components/ProGate";

export default function Explore() {
  const [region, setRegion] = useState("North America");
  const [cabin, setCabin] = useState("business");
  const isPro = useIsPro();
  const deals = useMemo(() => exploreDeals(region, cabin, 24), [region, cabin]);
  const free = deals.filter((d) => !isDateLocked(d.date, isPro));
  const locked = deals.filter((d) => isDateLocked(d.date, isPro));

  return (
    <main className="min-h-[70vh] bg-slate-50">
      <Seo title="Explore award availability by region | FindMyFlight" description="Instantly see every award flight departing from any region. Sort by cabin and find the best options." path="/explore" />
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-8">
          <div className="flex items-center gap-2 text-blue-600"><Compass className="h-5 w-5" /><span className="text-sm font-semibold uppercase tracking-wide">Explore</span></div>
          <h1 className="mt-2 text-3xl font-extrabold text-slate-900">Explore award deals</h1>
          <p className="mt-2 max-w-2xl text-slate-600">Discover where your points can take you. Browse real award availability departing from an entire region, sorted by your favourite cabin.</p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Region</span>
              <select value={region} onChange={(e) => setRegion(e.target.value)} className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium outline-none focus:border-blue-500">
                {REGIONS.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-1.5">
              <Filter className="h-4 w-4 text-slate-400" />
              {CABINS.map((c) => (
                <button key={c.key} onClick={() => setCabin(c.key)} className={`rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors ${cabin === c.key ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-blue-300"}`}>{c.label}</button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-5 py-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {free.map((d) => <FlightCard key={d.id} d={d} />)}
        </div>
        {locked.length > 0 && (
          <div className="mt-8">
            <p className="mb-3 text-sm font-semibold text-slate-700" data-testid="locked-results-heading">{locked.length} more deal{locked.length === 1 ? "" : "s"} departing beyond the next {FREE_WINDOW_DAYS} days</p>
            <ProGate locked count={locked.length}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {locked.slice(0, 8).map((d) => <FlightCard key={d.id} d={d} savable={false} />)}
              </div>
            </ProGate>
          </div>
        )}
      </div>
    </main>
  );
}
